import React from 'react';

/**
 * Fix Registry — her fix tipi kendini burada tanimlar. FixModule shell'i bu
 * tanimlari kullanarak listeleme / tablo / parametre duzenleme / apply
 * akisini GENERIC yurutur. Yeni bir fix eklemek icin:
 *   1) Buraya bir kayit ekle (operationKey + columns + toTarget).
 *   2) Backend'de helpers/HRFix.py FIX_HANDLERS'a ayni id ile handler ekle.
 *
 * Kayit sozlesmesi:
 *   id          : registry/dispatch anahtari (backendId varsayilan = id)
 *   backendId   : HRFix.FIX_HANDLERS anahtari (ornek 'ST01')
 *   error_id    : MessageCodes error_id (results sayfasi eslesmesi)
 *   operationKey: check operasyonunun child.key'i (bulgularin geldigi yer)
 *   label/info  : UI metinleri
 *   getItems    : (operationResult[]) => rawItem[]
 *   isFixable   : (rawItem) => bool   (aralik disi / fixlenebilir mi)
 *   rowKey      : (rawItem) => string (benzersiz satir anahtari)
 *   defaultParams: (settings) => ({...})  satir basina duzenlenebilir parametreler
 *   columns     : sutun tanimlari. Iki tip:
 *                  - display: {key, title, width, render(row)}
 *                  - param:   {key, title, width, param:{type:'number', min, step}}
 *                    (param.key, defaultParams icindeki alan adidir)
 *   toTarget    : (row) => backend target objesi
 */

const ratioCell = (val, ok) =>
    ok === false
        ? <span style={{color: 'red'}}>{val ?? 'None'}</span>
        : <span>{val ?? 'None'}</span>;

const ST01 = {
    id: 'ST01',
    backendId: 'ST01',
    error_id: 'ST 01',
    operationKey: 'B5',
    label: 'Cont/Exp Ratio around Structure',
    info: 'Adjust ineffective stations on the bounding cross-sections so the contraction/expansion ratios meet the User Preference values.',
    getItems: (result) => (result || []).flatMap((r) => r?.data || []),
    isFixable: (s) =>
        s?.up_left_ratio_in_range === false ||
        s?.up_right_ratio_in_range === false ||
        s?.down_left_ratio_in_range === false ||
        s?.down_right_ratio_in_range === false,
    rowKey: (s) => String(s.cross_section),
    defaultParams: (settings) => ({
        target_contraction: Number(settings?.defaults?.contraction_ratio) || 1,
        target_expansion: Number(settings?.defaults?.expansion_ratio) || 1,
    }),
    columns: [
        {key: 'rs', title: 'RS', width: 90, render: (row) => row.raw.cross_section},
        {key: 'type', title: 'Type', width: 80, render: (row) => row.raw.type},
        {
            key: 'us', title: 'US ratios (L/R)', width: 130,
            render: (row) => (
                <>{ratioCell(row.raw.up_left_ratio, row.raw.up_left_ratio_in_range)} /{' '}
                    {ratioCell(row.raw.up_right_ratio, row.raw.up_right_ratio_in_range)}</>
            ),
        },
        {
            key: 'ds', title: 'DS ratios (L/R)', width: 130,
            render: (row) => (
                <>{ratioCell(row.raw.down_left_ratio, row.raw.down_left_ratio_in_range)} /{' '}
                    {ratioCell(row.raw.down_right_ratio, row.raw.down_right_ratio_in_range)}</>
            ),
        },
        {key: 'target_contraction', title: 'Cont', width: 90, param: {type: 'number', min: 0.1, step: 0.1}},
        {key: 'target_expansion', title: 'Exp', width: 90, param: {type: 'number', min: 0.1, step: 0.1}},
    ],
    toTarget: (row) => ({
        cross_section: row.raw.cross_section,
        target_contraction: row.params.target_contraction,
        target_expansion: row.params.target_expansion,
    }),
};

const ST06 = {
    id: 'ST06',
    backendId: 'ST06',
    error_id: 'ST 06',
    operationKey: 'B30',
    label: 'Ineffective Area around Structure',
    info: 'Adjust the DS cross-section ineffective elevations to act consistently with the US cross-section of the structure for all listed profiles (copies the US ineffective elevations to the DS).',
    getItems: (result) => (result || []).flatMap((r) => r?.data || []),
    // Check yalnizca tutarsiz yapilari raporlar — hepsi fixlenebilir.
    isFixable: () => true,
    rowKey: (s) => String(s.cross_section),
    defaultParams: () => ({}),
    columns: [
        {key: 'rs', title: 'RS', width: 90, render: (row) => row.raw.cross_section},
        {key: 'type', title: 'Type', width: 80, render: (row) => row.raw.type},
        {
            key: 'profiles', title: 'Inconsistent profiles', width: 280,
            render: (row) => (row.raw.profiles || []).join(', '),
        },
    ],
    toTarget: (row) => ({cross_section: row.raw.cross_section}),
};

// PS 00 — HEC-RAS Calculation Tolerances. Plan-seviyesi fix: plan'in hesaplama
// toleranslarini User Default'a ceker. Bulgu OV4 check'inden gelir; OV4 operasyon
// sonucuna RunOperations icinde gomulen {tolerances, user_default} okunur.
// Alanlar (label/sira/range) OV4.js ile ayni.
const PS00_TOL_FIELDS = [
    {field: 'stdStepTol',         label: 'Water surface calculation tol. (.0001 - .1 ft)'},
    {field: 'criticalTol',        label: 'Critical depth calculation tol. (.0001 - .1 ft)'},
    {field: 'numOfStdStepTrials', label: 'Maximum number of iterations (3 - 40)'},
    {field: 'maxErrorTol',        label: 'Maximum difference tolerance (.1 - 1.0 ft)'},
    {field: 'flowTolRatio',       label: 'Flow tolerance factor (.0001 - .05)'},
    {field: 'splitFlowNTrial',    label: 'Maximum iteration in Split flow (3 - 100)'},
    {field: 'splitFlowRatio',     label: 'Flow tolerance factor in weir split flow (.0001 - .05)'},
    {field: 'splitFlowTol',       label: 'Max difference in junction split flow (.001 - .1 ft)'},
];

const _num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

const PS00 = {
    id: 'PS00',
    backendId: 'PS00',
    error_id: 'OV 04',
    operationKey: 'OV4',
    label: 'HEC-RAS Calculation Tolerances',
    info: 'Set the plan calculation tolerances to the User Default values. Only the tolerances different from the User Default are applied.',
    // OV4 sonucu: [{error_id:'OV 04', data:[{tolerances, user_default}]}]. Her tolerans
    // alani icin bir satir uret (ekrandaki User Default vs Plan Value tablosu gibi).
    getItems: (result) => {
        const payload = (result || [])[0]?.data?.[0] || {};
        const tol = payload.tolerances;
        const ud = payload.user_default;
        if (!tol || !ud) return [];
        return PS00_TOL_FIELDS.map(({field, label}) => {
            const planVal = tol[field];
            const userVal = ud[field];
            return {
                cross_section: 'Plan',
                field,
                label,
                plan_value: planVal,
                user_default: userVal,
                differs: _num(planVal) !== _num(userVal),
            };
        });
    },
    // Yalnizca User Default'tan farkli olan alanlar fixlenebilir / default secili.
    isFixable: (it) => it.differs === true,
    rowKey: (it) => `PS00_${it.field}`,
    defaultParams: () => ({}),
    columns: [
        {key: 'label', title: 'Calculation', width: 320, render: (row) => row.raw.label},
        {key: 'user_default', title: 'User Default', width: 110, render: (row) => row.raw.user_default ?? 'None'},
        {
            key: 'plan_value', title: 'Plan Value', width: 110,
            render: (row) => row.raw.differs
                ? <span style={{color: 'red'}}>{row.raw.plan_value ?? 'None'}</span>
                : <span>{row.raw.plan_value ?? 'None'}</span>,
        },
    ],
    // Backend, degeri tek dogruluk kaynagi olan settings'ten okur; field yeterli.
    toTarget: (row) => ({field: row.raw.field, value: row.raw.user_default}),
};

// RT 05 — Horizontally varied Manning's n value. Bulgu B21 (Manning's n) check'inden
// gelir; ayni operasyon RT 03/RT 04/RT-05 gruplarini birlikte dondurur, bu yuzden
// getItems YALNIZCA 'RT-05' grubunu alir. Fix, bank'a en yakin HvM istasyonunu Bank
// Station'a esitler (backend bunu geometriden yeniden hesaplar; field yeterli).
const RT05 = {
    id: 'RT05',
    backendId: 'RT05',
    error_id: 'RT-05',
    operationKey: 'B21',
    label: 'Horizontally varied Manning\'s n value',
    info: 'Adjust the HvM (horizontally varied Manning\'s n) stations to be consistent with the Bank Stations for the listed cross sections.',
    getItems: (result) => (result || []).filter((r) => r?.error_id === 'RT-05').flatMap((r) => r?.data || []),
    // Check yalnizca tutarsiz kesitleri raporlar -> hepsi fixlenebilir.
    isFixable: () => true,
    rowKey: (s) => String(s.cross_section),
    defaultParams: () => ({}),
    columns: [
        {key: 'rs', title: 'RS', width: 90, render: (row) => row.raw.cross_section},
        {key: 'left_bank', title: 'L Bank', width: 90, render: (row) => row.raw.left_bank ?? 'None'},
        {
            key: 'left_hvm', title: 'L HvM', width: 90,
            render: (row) => ratioCell(row.raw.left_station, _num(row.raw.left_station) === _num(row.raw.left_bank)),
        },
        {key: 'right_bank', title: 'R Bank', width: 90, render: (row) => row.raw.right_bank ?? 'None'},
        {
            key: 'right_hvm', title: 'R HvM', width: 90,
            render: (row) => ratioCell(row.raw.right_station, _num(row.raw.right_station) === _num(row.raw.right_bank)),
        },
    ],
    toTarget: (row) => ({cross_section: row.raw.cross_section}),
};

// ST 02 — Bridge Modeling Methods. Bulgu B7 check'inden gelir; ayni operasyon
// ST 02 / ST 03 / ST 04 gruplarini birlikte dondurur, bu yuzden getItems YALNIZCA
// 'ST 02' grubunu alir. Hedefleme ACIKLIK bazlidir: her kopru aciklik (message)
// ayri bir satirdir (cross_section + bridge_number). Fix `BR Coef=` satirini
// onerilen modelleme ayarlarina ceker; Cd ve Pier Shape K kullanici tarafindan
// (sekil dropdown'i VEYA manuel sayisal giris ile) degistirilebilir.

// HEC-RAS Drag Coefficient Cd — pier burun sekline gore (1D Tech Ref, Low Flow).
const ST02_CD_SHAPES = [
    {value: 'circular', label: 'Circular pier', derived: 1.20},
    {value: 'elong_semicircular', label: 'Elongated, semi-circular ends', derived: 1.33},
    {value: 'ellipse_2_1', label: 'Elliptical 2:1', derived: 0.60},
    {value: 'ellipse_4_1', label: 'Elliptical 4:1', derived: 0.32},
    {value: 'ellipse_8_1', label: 'Elliptical 8:1', derived: 0.29},
    {value: 'square', label: 'Square nose', derived: 2.00},
    {value: 'tri_30', label: 'Triangular nose 30°', derived: 1.00},
    {value: 'tri_60', label: 'Triangular nose 60°', derived: 1.39},
    {value: 'tri_90', label: 'Triangular nose 90°', derived: 1.60},
    {value: 'tri_120', label: 'Triangular nose 120°', derived: 1.72},
];

// Yarnell Pier Shape K — pier sekline gore (Cd'den FARKLI taksonomi).
const ST02_K_SHAPES = [
    {value: 'semicircular', label: 'Semicircular nose & tail', derived: 0.90},
    {value: 'twin_diaphragm', label: 'Twin-cylinder + diaphragm', derived: 0.95},
    {value: 'twin_nodia', label: 'Twin-cylinder, no diaphragm', derived: 1.05},
    {value: 'tri_90', label: '90° triangular nose & tail', derived: 1.05},
    {value: 'square', label: 'Square nose & tail', derived: 1.25},
    {value: 'tenpile', label: 'Ten-pile trestle bent', derived: 2.50},
];

const ST02 = {
    id: 'ST02',
    backendId: 'ST02',
    error_id: 'ST 02',
    operationKey: 'B7',
    label: 'Bridge Modeling Methods',
    info: 'Normalize the recommended bridge modeling methods (Energy + Momentum, Highest Energy answer, Pressure and/or Weir, Submerged Inlet+Outlet, and — when the bridge has piers — Yarnell + Pier Shape K). Coef Drag Cd and Pier Shape K can be set per opening via the pier-shape dropdown or by typing a value.',
    // B7 result ST 02 / ST 03 / ST 04 gruplarini birlikte dondurur -> sadece ST 02.
    // Her yapinin messages[] dizisi ihlal eden ACIKLIKLARI tasir -> aciklik basina satir.
    getItems: (result) => (result || [])
        .filter((r) => r?.error_id === 'ST 02')
        .flatMap((r) => (r?.data || []).flatMap((s) =>
            (s?.messages || []).map((m) => ({
                cross_section: s.cross_section,
                bridge_number: m.number,
                has_pier: !!m.has_pier,
                missing_methods: m.missing_methods || [],
                missing_values: m.missing_values || [],
                type: 'Bridge',
            }))
        )),
    // Check yalnizca ihlal eden aciklik icin message uretir -> hepsi fixlenebilir.
    isFixable: () => true,
    rowKey: (s) => `${s.cross_section}#${s.bridge_number}`,
    defaultParams: () => ({
        pier_shape: 'circular',
        coef_drag_cd: 1.20,
        pier_k_shape: 'semicircular',
        pier_shape_k: 0.90,
        submerged: 0.80,
    }),
    columns: [
        {key: 'rs', title: 'RS', width: 80, render: (row) => row.raw.cross_section},
        {key: 'opening', title: 'Opening', width: 70, render: (row) => `#${row.raw.bridge_number}`},
        {
            key: 'missing', title: 'Missing methods / values', width: 220,
            render: (row) => [...(row.raw.missing_methods || []), ...(row.raw.missing_values || [])].join(', '),
        },
        {key: 'pier_shape', title: 'Pier Shape (Cd)', width: 180, param: {type: 'select', link: 'coef_drag_cd', options: ST02_CD_SHAPES}},
        {key: 'coef_drag_cd', title: 'Cd', width: 80, param: {type: 'number', min: 0, step: 0.01}},
        {key: 'pier_k_shape', title: 'Pier Shape (K)', width: 180, pierOnly: true, param: {type: 'select', link: 'pier_shape_k', options: ST02_K_SHAPES}},
        {key: 'pier_shape_k', title: 'K', width: 80, pierOnly: true, param: {type: 'number', min: 0, step: 0.01}},
        {key: 'submerged', title: 'Submerged Cd', width: 100, param: {type: 'number', min: 0, step: 0.05}},
    ],
    toTarget: (row) => ({
        cross_section: row.raw.cross_section,
        bridge_number: row.raw.bridge_number,
        coef_drag_cd: row.params.coef_drag_cd,
        pier_shape_k: row.raw.has_pier ? row.params.pier_shape_k : null,
        submerged: row.params.submerged,
    }),
};

// ST 03 — Maximum Submergence Ratio (bridge). Bulgu B7 check'inden gelir (ST 02 /
// ST 03 / ST 04 birlikte doner) -> yalnizca 'ST 03'. Yapi (structure) bazlidir.
// Fix, Deck Dist Width satirindaki Max Submergence degerini hedef orana ceker;
// hedef User Pref max_submergence_ratio'dan gelir (yoksa 0.98), kullanici degistirebilir.
const ST03 = {
    id: 'ST03',
    backendId: 'ST03',
    error_id: 'ST 03',
    operationKey: 'B7',
    label: 'Bridge Max Submergence Ratio',
    info: 'Set the bridge Maximum Submergence Ratio to the recommended value (User Preference max submergence ratio, default 0.98). Editable per structure.',
    getItems: (result) => (result || [])
        .filter((r) => r?.error_id === 'ST 03')
        .flatMap((r) => r?.data || []),
    // Check yalnizca default'tan farkli yapilari raporlar -> hepsi fixlenebilir.
    isFixable: () => true,
    rowKey: (s) => String(s.cross_section),
    defaultParams: (settings) => ({
        target_submergence: Number(settings?.defaults?.max_submergence_ratio) || 0.98,
    }),
    columns: [
        {key: 'rs', title: 'RS', width: 100, render: (row) => row.raw.cross_section},
        {key: 'current', title: 'Current ratio', width: 110, render: (row) => row.raw.ratio ?? 'None'},
        {key: 'target_submergence', title: 'Target', width: 90, param: {type: 'number', min: 0, step: 0.01}},
    ],
    toTarget: (row) => ({
        cross_section: row.raw.cross_section,
        target_submergence: row.params.target_submergence,
    }),
};

// ST 04 — Maximum Submergence Ratio (culvert). ST 03 ile ayni; tek fark error_id
// 'ST 04' (B7 result icindeki culvert grubu) ve yapinin culvert olmasi.
const ST04 = {
    id: 'ST04',
    backendId: 'ST04',
    error_id: 'ST 04',
    operationKey: 'B7',
    label: 'Culvert Max Submergence Ratio',
    info: 'Set the culvert Maximum Submergence Ratio to the recommended value (User Preference max submergence ratio, default 0.98). Editable per structure.',
    getItems: (result) => (result || [])
        .filter((r) => r?.error_id === 'ST 04')
        .flatMap((r) => r?.data || []),
    isFixable: () => true,
    rowKey: (s) => String(s.cross_section),
    defaultParams: (settings) => ({
        target_submergence: Number(settings?.defaults?.max_submergence_ratio) || 0.98,
    }),
    columns: [
        {key: 'rs', title: 'RS', width: 100, render: (row) => row.raw.cross_section},
        {key: 'current', title: 'Current ratio', width: 110, render: (row) => row.raw.ratio ?? 'None'},
        {key: 'target_submergence', title: 'Target', width: 90, param: {type: 'number', min: 0, step: 0.01}},
    ],
    toTarget: (row) => ({
        cross_section: row.raw.cross_section,
        target_submergence: row.params.target_submergence,
    }),
};

// RT 01 — Contraction/Expansion Coefficient. Bulgu B4 check'inden gelir (RT 01 /
// RT 02 birlikte doner) -> yalnizca 'RT 01' (yapi etrafindaki kesitler). Fix, hedef
// kesitlerin Exp/Cntr katsayilarini onerilen yapi-kesiti degerlerine ceker; varsayilan
// User Pref cont/exp_coef_structure_xs'ten gelir, kullanici satir bazinda degistirebilir.
const RT01 = {
    id: 'RT01',
    backendId: 'RT01',
    error_id: 'RT 01',
    operationKey: 'B4',
    label: 'Cont/Exp Coefficient (structure XS)',
    info: 'Set the contraction/expansion coefficients of the cross sections bounding structures to the recommended structure-XS values (User Preference cont/exp coef structure_xs). Editable per cross section.',
    getItems: (result) => (result || [])
        .filter((r) => r?.error_id === 'RT 01')
        .flatMap((r) => r?.data || []),
    // Check yalnizca onerilen degerden farkli kesitleri raporlar -> hepsi fixlenebilir.
    isFixable: () => true,
    rowKey: (s) => String(s.cross_section),
    defaultParams: (settings) => ({
        target_cont: Number(settings?.defaults?.cont_coef_structure_xs) || 0.3,
        target_exp: Number(settings?.defaults?.exp_coef_structure_xs) || 0.5,
    }),
    columns: [
        {key: 'rs', title: 'RS', width: 90, render: (row) => row.raw.cross_section},
        {key: 'structure', title: 'Structure', width: 110, render: (row) => row.raw.culvert ?? '-'},
        {key: 'sec', title: 'Sec', width: 55, render: (row) => row.raw.number ?? '-'},
        {
            key: 'current', title: 'Current Cont/Exp', width: 130,
            render: (row) => `${row.raw.cont ?? 'None'} / ${row.raw.exp ?? 'None'}`,
        },
        {key: 'target_cont', title: 'Cont', width: 80, param: {type: 'number', min: 0, step: 0.1}},
        {key: 'target_exp', title: 'Exp', width: 80, param: {type: 'number', min: 0, step: 0.1}},
    ],
    toTarget: (row) => ({
        cross_section: row.raw.cross_section,
        target_cont: row.params.target_cont,
        target_exp: row.params.target_exp,
    }),
};

// RT 02 — Contraction/Expansion Coefficient (dogal kesitler). RT 01 ile ayni mantik;
// fark: B4 result icindeki 'RT 02' grubu, varsayilanlar User Pref *_coef_natural_xs'ten
// gelir ve veri kaleminde structure/section bilgisi yoktur.
const RT02 = {
    id: 'RT02',
    backendId: 'RT02',
    error_id: 'RT 02',
    operationKey: 'B4',
    label: 'Cont/Exp Coefficient (natural XS)',
    info: 'Set the contraction/expansion coefficients of natural cross sections to the recommended values (User Preference cont/exp coef natural_xs). Editable per cross section.',
    getItems: (result) => (result || [])
        .filter((r) => r?.error_id === 'RT 02')
        .flatMap((r) => r?.data || []),
    isFixable: () => true,
    rowKey: (s) => String(s.cross_section),
    defaultParams: (settings) => ({
        target_cont: Number(settings?.defaults?.cont_coef_natural_xs) || 0.1,
        target_exp: Number(settings?.defaults?.exp_coef_natural_xs) || 0.3,
    }),
    columns: [
        {key: 'rs', title: 'RS', width: 100, render: (row) => row.raw.cross_section},
        {
            key: 'current', title: 'Current Cont/Exp', width: 130,
            render: (row) => `${row.raw.cont ?? 'None'} / ${row.raw.exp ?? 'None'}`,
        },
        {key: 'target_cont', title: 'Cont', width: 80, param: {type: 'number', min: 0, step: 0.1}},
        {key: 'target_exp', title: 'Exp', width: 80, param: {type: 'number', min: 0, step: 0.1}},
    ],
    toTarget: (row) => ({
        cross_section: row.raw.cross_section,
        target_cont: row.params.target_cont,
        target_exp: row.params.target_exp,
    }),
};

// XY 06 — Bridge skew angle. Bulgu B12 (skew) check'inden gelir; bu operasyon birden
// cok grup dondurur (XY 06 / XY 17 / SKW ...), bu yuzden yalnizca 'XY 06' alinir. Fix,
// koprunun OLCULEN skew acisini TANIMLI skew alanina (Deck Dist Width skew) yazar.
// Varsayilan hedef SATIR-BAZLIDIR: olculen deger (skew_map, yoksa skew); kullanici
// degistirebilir. defaultParams ikinci arg olarak ham bulguyu alir.
const XY06 = {
    id: 'XY06',
    backendId: 'XY06',
    error_id: 'XY 06',
    operationKey: 'B12',
    label: 'Bridge Skew Angle',
    info: 'Define the measured skew angle on the skewed bridges (writes the measured skew — map skew when available, otherwise computed structure skew — into the bridge Deck skew). Editable per structure.',
    getItems: (result) => (result || [])
        .filter((r) => r?.error_id === 'XY 06')
        .flatMap((r) => r?.data || []),
    isFixable: () => true,
    rowKey: (s) => String(s.cross_section),
    // Olculen deger satira ozgu -> defaultParams ham bulgudan okur (skew_map ?? skew).
    defaultParams: (_settings, raw) => ({
        target_skew: Number(raw?.skew_map ?? raw?.skew) || 0,
    }),
    columns: [
        {key: 'rs', title: 'RS', width: 110, render: (row) => row.raw.cross_section},
        {key: 'skew', title: 'Measured', width: 90, render: (row) => row.raw.skew ?? 'None'},
        {key: 'skew_map', title: 'Map skew', width: 90, render: (row) => row.raw.skew_map ?? 'None'},
        {key: 'defined', title: 'Defined', width: 90, render: (row) => row.raw.defined_skew ?? 'None'},
        {key: 'target_skew', title: 'Target', width: 90, param: {type: 'number', step: 0.1}},
    ],
    toTarget: (row) => ({
        cross_section: row.raw.cross_section,
        target_skew: row.params.target_skew,
    }),
};

// XS 06 — Bank station elevations inconsistent. Bulgu XS4 (check_bank_sta_elev)
// operasyonundan gelir (XS 06 / XS 08 birlikte doner) -> yalnizca 'XS 06'. Fix
// deterministiktir (parametre yok): her iki bank kotunu ortalamaya getirir, yeni
// bank station'larini sta/elev interpolasyonu ile bulur ve vertex olarak ekler.
const fmtSE = (p) => (Array.isArray(p) ? `${p[0]} / ${p[1]}` : 'None');
const XS06 = {
    id: 'XS06',
    backendId: 'XS06',
    error_id: 'XS 06',
    operationKey: 'XS4',
    label: 'Bank Station Elevations',
    info: "Level the left and right bank station elevations: move each bank station along the cross-section profile (interpolating a new sta/elev point) so both banks meet at their mean elevation, removing the bank-line slope.",
    getItems: (result) => (result || [])
        .filter((r) => r?.error_id === 'XS 06')
        .flatMap((r) => r?.data || []),
    isFixable: () => true,
    rowKey: (s) => String(s.cross_section),
    defaultParams: () => ({}),
    columns: [
        {key: 'rs', title: 'RS', width: 110, render: (row) => row.raw.cross_section},
        {key: 'slope', title: 'BS slope', width: 90, render: (row) => row.raw.slope ?? 'None'},
        {key: 'lbank', title: 'L bank (sta/elev)', width: 150, render: (row) => fmtSE(row.raw.left_sta_elev)},
        {key: 'rbank', title: 'R bank (sta/elev)', width: 150, render: (row) => fmtSE(row.raw.right_sta_elev)},
        {key: 'delta', title: 'Δ elev', width: 80, render: (row) => row.raw.elev_diff ?? 'None'},
    ],
    toTarget: (row) => ({cross_section: row.raw.cross_section}),
};

export const FIXES = [ST01, ST02, ST03, ST04, ST06, RT01, RT02, XY06, XS06, PS00, RT05];

export const getFix = (id) => FIXES.find((f) => f.id === id);
export const getFixByErrorId = (errorId) => FIXES.find((f) => f.error_id === errorId);

// Bir check geçmişinde bu fix için bulgu var mı? -> {def, items, fixableCount}
export const scanHistoryForFix = (history, def) => {
    if (!history || !def) return null;
    for (const op of history.operations || []) {
        for (const child of op.children || []) {
            if (child.key === def.operationKey) {
                const result = child.originalData?.result || child.data?.result || [];
                const items = def.getItems(result);
                if (items.length === 0) return null;
                return {def, items, fixableCount: items.filter(def.isFixable).length};
            }
        }
    }
    return null;
};
